/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React from 'react';

import { css, jsx } from '@atlaskit/css';
import { BLOCK_CONTROLS_LEFT_SURFACE } from '@atlaskit/editor-common/block-controls/surface-keys';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import {
	SurfaceRenderer,
	willSurfaceRender,
} from '@atlaskit/editor-ui-control-model/surface-renderer';
import type { SurfaceContext } from '@atlaskit/editor-ui-control-model/types';
import { token } from '@atlaskit/tokens';

import type { BlockControlsPlugin } from '../blockControlsPluginType';

import type { BlockControlsLeftSurfaceTarget } from './block-controls-left-surface-targets';
import type { SurfacePlacement } from './utils/get-surface-placement';
import { VisibilityContainer } from './visibility-container';

const leftSurfaceStyles = css({
	alignItems: 'center',
	display: 'flex',
	gap: token('space.025'),
	position: 'absolute',
	zIndex: 100,
});

type BlockControlsLeftSurfaceProps = {
	api: ExtractInjectionAPI<BlockControlsPlugin>;
	components: React.ComponentProps<typeof SurfaceRenderer>['components'];
	forceVisibleOnMouseOut: boolean;
	placement: SurfacePlacement | undefined;
	source: BlockControlsLeftSurfaceTarget['source'];
	surfaceContext: SurfaceContext;
};

/** Renders one left block-controls surface at a document position. */
export const BlockControlsLeftSurface = ({
	api,
	components,
	forceVisibleOnMouseOut,
	placement,
	source,
	surfaceContext,
}: BlockControlsLeftSurfaceProps): React.JSX.Element | null => {
	if (!placement || !willSurfaceRender(components, BLOCK_CONTROLS_LEFT_SURFACE, surfaceContext)) {
		return null;
	}
	const surface = (
		<SurfaceRenderer
			components={components}
			surface={BLOCK_CONTROLS_LEFT_SURFACE}
			surfaceContext={surfaceContext}
		/>
	);

	return (
		<div
			css={leftSurfaceStyles}
			data-editor-block-controls-surface
			data-testid={
				source === 'stored'
					? 'block-controls-left-surface-stored'
					: source === 'active'
						? 'block-controls-left-surface-active'
						: 'block-controls-left-surface'
			}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Placement is resolved from the target node's runtime DOM geometry.
			style={placement}
		>
			<VisibilityContainer
				api={api}
				controlSide="left"
				forceVisibleOnMouseOut={forceVisibleOnMouseOut}
				shouldUseDisplayContents
			>
				{surface}
			</VisibilityContainer>
		</div>
	);
};
